import { Dispatch, ForwardedRef, forwardRef, SetStateAction } from "react";

import { MarketingAssetsConstants, OrganisationPermissionConstants } from "../../../../../../constants";
import {
  ActionButtons,
  Column,
  ErrorBox,
  KanaTooltip,
  Row,
  SingleFileUpload,
  TextArea,
  Toggle,
} from "../../../../../../widget";
import {
  FormAboutUsData,
  MarketingAssetsFormChangedAction,
  MarketingAssetsSubmitHandler,
  UpdateListingType,
} from "../../models";
import { useAboutUsForm } from "./useAboutUsForm";

interface AboutUsFormProps {
  objectUuid: string;
  objectType: string;
  objectKey: string;
  updateListing: UpdateListingType;
  aboutUsDefaultValues: FormAboutUsData;
  aboutUsOldDefaultValues: FormAboutUsData;
  listingRowVersion: number;
  setListingRowVersion: Dispatch<SetStateAction<number>>;
  dispatch: Dispatch<MarketingAssetsFormChangedAction>;
  getCurrentFormsData: (formWhichWasSaved: string) => string;
}

export const AboutUsForm = forwardRef(
  (
    {
      objectUuid,
      objectType,
      objectKey,
      updateListing,
      aboutUsDefaultValues,
      aboutUsOldDefaultValues,
      listingRowVersion,
      setListingRowVersion,
      dispatch,
      getCurrentFormsData,
    }: AboutUsFormProps,
    forwardedRef: ForwardedRef<MarketingAssetsSubmitHandler>
  ): JSX.Element => {
    const {
      teamPhotoUploadRef,
      enabled,
      content,
      errors,
      teamImageUrl,
      setEnabled,
      setContent,
      handleSetTeamPhoto,
      handleCancel,
      handleSubmit,
      hasPermission,
    } = useAboutUsForm(
      objectUuid,
      objectType,
      objectKey,
      updateListing,
      aboutUsDefaultValues,
      aboutUsOldDefaultValues,
      listingRowVersion,
      setListingRowVersion,
      dispatch,
      forwardedRef,
      getCurrentFormsData
    );

    return (
      <>
        <Row spacingV="ll">
          <Column span={4}>
            <h2>About us</h2>
          </Column>
        </Row>
        {errors && errors.length > 0 && (
          <Row spacingV="ml">
            <Column span={4}>
              <ErrorBox
                title="Please solve the below issues before saving"
                text={errors.map((error) => error.message)}
              />
            </Column>
          </Row>
        )}
        <form onSubmit={(e) => handleSubmit(e)}>
          <Row spacingV="ll">
            <Column span={12}>
              <Toggle label="Show this section on organisation profile" value={enabled} onChange={setEnabled} />
            </Column>
          </Row>
          <Row spacingV="l">
            <Column span={6}>
              <SingleFileUpload
                ref={teamPhotoUploadRef}
                label="Team photo"
                disabled={!enabled}
                informationLabel="The image should be 1920px x 450px"
                previewUrl={teamImageUrl}
                onChange={async (f) => {
                  await handleSetTeamPhoto(f);
                }}
              />
            </Column>
          </Row>
          <Row spacingV="ll">
            <Column span={10}>
              <TextArea
                label="Content"
                name="hero-content"
                disabled={!enabled}
                tooltip={
                  <KanaTooltip
                    tooltipHeader="What is the about us content used for?"
                    tooltipText="This will be displayed at the bottom of your developer profile and should offer an overview of your business and it's people."
                  />
                }
                value={content || ""}
                onChange={(v) => {
                  setContent(v);
                }}
                maxLength={MarketingAssetsConstants.ABOUT_US_CONTENT_MAX_LENGTH}
                displayCharacterCount
              />
            </Column>
          </Row>
          {hasPermission(OrganisationPermissionConstants.WRITE) && (
            <Row>
              <Column span={4}>
                <ActionButtons onCancel={handleCancel} />
              </Column>
            </Row>
          )}
        </form>
      </>
    );
  }
);
